import React from 'react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Clock, BookOpen, Trophy } from 'lucide-react'
import placeholderImg from '../../../../../../resources/icon.png'
import { exercisesAPI } from '../../../api/exercises'
import { appInfo } from '@renderer/config/appinfo'
import { calcDurationMinutes, formatDuration } from '@renderer/utils/duration'

type Activity = {
   exercise: { id: number; title: string }
  lab_module: { id: number; course_id: number }
  total_modules: number
  current_module: number
  thumbnail: string | null
  next_module: { id: number; title: string } | null
}

export const CurrentActivity: React.FC = () => {

    const [activity, setActivity] = useState<Activity | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [loading, setLoading] = useState(true)
    const [nextModuleDuration, setNextModuleDuration] = useState<string>('—')
    const navigate = useNavigate()

    useEffect(() => {
      const fetchCourses = async (): Promise<void> => {
        try {
          const data = await exercisesAPI.getCurrentExercise()
          setActivity(data)
          if (data?.next_module) {
            exercisesAPI.getModuleExercises(String(data.next_module.id))
              .then((exercises: unknown[]) => {
                setNextModuleDuration(formatDuration(calcDurationMinutes(exercises.length)))
              })
              .catch(() => setNextModuleDuration('—'))
          }
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to load courses')
        } finally {
          setLoading(false)
        }
      }

      fetchCourses()
    }, [])

  if (loading) return <div>Loading...</div>
  if (error) return <div>Error: {error}</div>
  if (!activity) return (
    <div className="flex flex-col items-center justify-center h-full gap-4 py-8 text-center">
    <div className="w-14 h-14 bg-[#f5ede0] rounded-2xl flex items-center justify-center">
      <BookOpen className="w-7 h-7 text-[#7a6020]" />
    </div>
    <div className="grid gap-1">
      <h2 className="text-lg font-bold text-[#181820]">No activity yet</h2>
      <p className="text-sm text-[#5A5A6D]">Start an exercise to track your progress here</p>
    </div>
    </div>
  )

  return (
    <div className="relative w-full h-full min-h-[350px] overflow-hidden" style={{ borderRadius: '16px' }}>
      {/* Background image */}
      <img
        src={activity.thumbnail ? `${appInfo.apiDomain}/storage/${activity.thumbnail}` : placeholderImg}
        alt="Exercise"
        className="absolute inset-0 w-full h-full object-cover"
        onError={(e) => { e.currentTarget.src = placeholderImg }}
      />

      {/* Dark overlay */}
      <div
        className="absolute inset-0"
        style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.55) 55%, rgba(0,0,0,0.18) 100%)' }}
      />

      {/* Content */}
      <div className="relative h-full flex flex-col justify-between p-8" style={{ minHeight: '350px' }}>
        {/* Top */}
        <div className="flex flex-col gap-4">
          {/* Badge */}
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 w-fit"
            style={{ borderRadius: '999px', background: 'rgba(0,0,0,0.45)', border: '1px solid rgba(255,255,255,0.18)', backdropFilter: 'blur(8px)' }}
          >
            <span className="w-2 h-2 rounded-full" style={{ background: '#facc15' }} />
            <Clock className="w-3.5 h-3.5" style={{ color: '#facc15' }} />
            <span className="text-xs font-semibold text-white">
              Module {activity.current_module} of {activity.total_modules}
            </span>
          </div>

          {/* Title */}
          <h2 className="text-3xl font-bold text-white leading-tight" style={{ maxWidth: '65%' }}>
            {activity.exercise.title}
          </h2>

          {/* Tags */}
          <div className="flex gap-2 flex-wrap">
            <span
              className="px-3 py-1 text-xs font-semibold text-white"
              style={{ borderRadius: '999px', border: '1px solid rgba(255,255,255,0.28)', background: 'rgba(0,0,0,0.22)' }}
            >
              Ongoing Exercise
            </span>
            <span
              className="inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold text-white"
              style={{ borderRadius: '999px', border: '1px solid rgba(255,255,255,0.28)', background: 'rgba(0,0,0,0.22)' }}
            >
              <BookOpen className="w-3 h-3" />
              {activity.total_modules} Modules
            </span>
          </div>
        </div>

        {/* Bottom */}
        <div className="flex flex-col gap-4">
          {/* Resume button */}
          <button
            onClick={() => navigate(`/exercise/${activity.exercise.id}/chat`)}
            className="inline-flex items-center gap-2 px-6 py-3 font-bold text-sm text-white transition-all w-fit hover:opacity-90"
            style={{ borderRadius: '12px', background: '#7a6020' }}
          >
            Resume Learning
          </button>

          {/* Next Recommended Module */}
          <div
            className="flex items-center gap-4 p-4 w-full"
            style={{ background: 'rgba(0,0,0,0.45)', borderRadius: '14px', border: '1px solid rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)' }}
          >
            <div className="w-9 h-9 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0">
              <Trophy className="w-4 h-4 text-[#facc15]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-bold text-white/50 uppercase tracking-widest mb-0.5">Next Module</p>
              <p className="text-sm font-bold text-white truncate">{activity.next_module ? activity.next_module.title : 'No more modules'}</p>
              <p className="text-xs text-white/50">Est. {nextModuleDuration}</p>
            </div>
            {activity.next_module && (
              <button
                onClick={() => activity.next_module && navigate(`/course/${activity.lab_module.course_id}/module/${activity.next_module.id}/exercises`)}
                className="flex-shrink-0 px-4 py-1.5 text-xs font-bold text-[#facc15] hover:text-white transition-colors"
              >
                Start →
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
